import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import type { ClassValue } from 'clsx';

/**
 * Plain `twMerge` only recognises Tailwind's own class names - it has no way to know that
 * this product's custom theme keys are the *same kind of thing* as the stock ones they sit
 * beside. `h-field` (2.5rem, this app's field height) does not read as a member of the
 * height group the way `h-10` does, so merging the two does not drop either one; both
 * survive in the class list, and whichever rule happens to come later in the generated
 * stylesheet wins - a coin flip that has nothing to do with the order the classes were
 * written in.
 *
 * That is not hypothetical: it is exactly why a `PLAYGROUND_CONTROL`-driven `h-8` override
 * silently lost to a component's own baked-in `h-field`, on every one of `Input`,
 * `Textarea` and `SelectTrigger` - the fix stayed in place but did nothing, because the
 * base class it was supposed to replace was never actually removed. This is that gap,
 * closed once: every custom key this app actually overrides gets registered in the same
 * group as its stock siblings, so `twMerge` drops the loser the way it already does for
 * `h-10` vs `h-9`.
 *
 * Extend this list the moment a *new* custom key is ever passed as an override target -
 * not preemptively for every theme key that exists, only the ones a call site needs
 * `cn()` to correctly replace.
 */
const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			h: ['h-field', 'h-control', 'h-header'],
			w: ['w-sidebar', 'w-sidebar-collapsed'],
			'font-size': ['text-2xs', 'text-3xs'],
			// Same gap as `h-field`: a component that bakes in `shadow-soft` kept it when a
			// call site passed `shadow-none`, because twMerge does not know the custom key
			// and so shipped both classes - with the custom utility winning.
			'shadow': ['shadow-soft', 'shadow-lift', 'shadow-popover', 'shadow-dialog'],
		},
	},
});

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * Classes that place or size a control belong to the wrapper the notch needs, not to the
 * control inside it - otherwise a call site's `flex-1` or `w-1/2` lands on the input and the
 * wrapper it now sits in ignores it.
 */
const LAYOUT_CLASS =
	/^-?(w-|min-w-|max-w-|flex-|basis-|grow|shrink|order-|col-|row-|self-|justify-self-|m[trblxye]?-|absolute$|relative$|fixed$|sticky$|inset|top-|left-|right-|bottom-|z-|hidden$|block$|inline)/;

// A variant prefix does not change what a class does, only when it applies: `sm:w-[16.25rem]`
// is still a width. Tested with the prefixes stripped, or a responsive width stays on the
// control while the wrapper keeps its own - which is how a 260px search input ended up
// painting 60px over the date picker inside its 200px wrapper.
const VARIANT_PREFIX = /^(?:[^:\s]+:)+/;

export function splitLayoutClasses(className?: string): [layout: string, control: string] {
	const parts = (className ?? '').split(/\s+/).filter(Boolean);
	const isLayout = (part: string) => LAYOUT_CLASS.test(part.replace(VARIANT_PREFIX, ''));
	return [parts.filter(isLayout).join(' '), parts.filter((part) => !isLayout(part)).join(' ')];
}
